// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductSelector, { ProductSelectorStyles } from "@rc/Overrides/Components/ProductSelector";
import { color, xScreen } from "@rc/XDL/Utilities";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    isFixedQuickOrder = "isFixedQuickOrder",
}

interface CompactQuickOrderProps extends WidgetProps {
    fields: {
        [fields.isFixedQuickOrder]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        excludeQuickOrderFromNavigation: state.links.pageLinks?.[0]?.children?.filter(
            page => page.type === "QuickOrderPage",
        )?.[0]?.excludeFromNavigation,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export type Props = CompactQuickOrderProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface compactQuickOrderStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    message?: TypographyProps;
    productSelector?: ProductSelectorStyles;
}

export const compactQuickOrderStyles: compactQuickOrderStyles = {
    container: {
        css: css`
            border: 1px solid ${color("common.border")};
            background-color: ${color("common.ui1")};
            height: 100%;
            padding: 15px;
            min-height: 360px;

            ${xScreen("lte", "md")} {
                min-height: 190px;
                margin-top: 60px;
            }

            ${xScreen("lte", "xs")} {
                min-height: 380px;
            }
        `,
    },
    title: {
        variant: "h4",
        css: css`
            font-weight: 500;
            color: ${color("common.ui6")};
            margin-bottom: 12px;
        `,
    },
    message: {
        size: "14px",
        lineHeight: "22px",
        css: css`
            color: ${color("text.main")};
            margin-bottom: 15px;
        `,
    },
    productSelector: {
        qtyGridItem: {
            css: css`
                align-items: center;
                ${xScreen("lte", "sm")} {
                    flex-basis: unset;
                }
            `,
        },
        buttonGridItem: {
            css: css`
                justify-content: flex-end;
                ${xScreen("lte", "sm")} {
                    flex-basis: unset;
                }
            `,
        },
        clearIcon: {
            css: css`
                right: 33px;
            `,
        },
        actionsContainer: {
            css: css`
                &&& {
                    ${xScreen("lte", "sm")} {
                        gap: 15px;
                        &&& > div {
                            gap: 15px;
                            justify-content: flex-end;
                        }
                    }
                }
            `,
        },
        backOrder: {
            width: 12,
            css: css`
                ${xScreen("lte", "sm")} {
                    &.compactQuickOrder ${GridItemStyle} {
                        justify-content: flex-end;
                        margin-left: 10px;
                    }
                    &.compactQuickOrder [class^="InventoryMessage"] {
                        width: 50%;
                        min-width: 50%;
                    }
                }
                ${xScreen("lte", "xs")} {
                    &.compactQuickOrder ${GridItemStyle} {
                        margin-left: 0;
                    }
                    &&&.compactQuickOrder [class^="InventoryMessage"] {
                        width: auto;
                        min-width: 100%;
                    }
                }
            `,
        },
    },
};

const styles = compactQuickOrderStyles;

const CompactQuickOrder = ({
    id,
    showAddToCartConfirmationDialog,
    addToCart,
    excludeQuickOrderFromNavigation,
    fields,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const { isFixedQuickOrder } = fields;

    const addProductToCart = async (productInfo: ProductInfo, product: ProductModel) => {
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            productInfo.pricing &&
            getUnitNetPrice(productInfo.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartline = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    if (excludeQuickOrderFromNavigation) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} className="compact-quick-order-container">
            <Typography as="h4" {...styles.title}>
                {translate("Quick Order")}
            </Typography>
            <Typography as="p" {...styles.message}>
                {siteMessage("Custom_Compact_Quickorder_msg")}
            </Typography>
            <ProductSelector
                selectButtonTitle={translate("Add to Cart")}
                onSelectProduct={addProductToCart}
                productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                extendedStyles={styles.productSelector}
                {...{ isCompactQuickOrder: true, isFixedQuickOrder }}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompactQuickOrder),
    definition: {
        displayName: "Compact Quick Order",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.isFixedQuickOrder,
                displayName: "Checkbox",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
        ],
    },
};

export default widgetModule;
